import React, { useState, useEffect, useMemo, useRef } from "react";
import "./BusinessZones.css";
import { PageLayout } from "../ui-components/PageLayout";
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Paper,
  Alert,
  InputAdornment,
} from "@mui/material";
import { NumericFormat } from "react-number-format";
import { Traffic as TrafficIcon, ArrowDownward } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../store/reducers";
import LoadingDialog from "../ui-components/Dialogs/LoadingDialog";
import { YearFilterSelect } from "../ui-components/YearFilterSelect";
import { CoAgentFilterSelect } from "../ui-components/CoAgentFilterSelect";
import { usePageCoAgentFilter } from "../hooks/usePageCoAgentFilter";
import {
  getBusinessZonesData,
  getBusinessZonesSettings,
  saveBusinessZonesSettings,
} from "../store/modules/analytics/analyticsApi";
import { formatCurrency } from "../utils/common";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const BusinessZones: React.FC = () => {
  // Redux state
  const { years } = useSelector((state: RootState) => state.transactionYear);
  const { hiddenFields } = useSelector(
    (state: RootState) => state.hiddenFields,
  );
  const { selectedCoAgentIds } = useSelector(
    (state: RootState) => state.coAgentFilter,
  );
  const { data: userData } = useSelector(
    (state: RootState) => state.commonData.userData,
  );
  const queryClient = useQueryClient();

  const coAgents = userData?.coAgents || [];

  // Page-specific co-agent filter
  const [effectiveCoAgentIds, setPageCoAgentIds, isGlobalFilterActive] =
    usePageCoAgentFilter("businessZones", selectedCoAgentIds);

  // Helper function to check if a category should be displayed
  const isCategoryVisible = (categoryName: string | undefined) => {
    if (!categoryName) return false;
    return !hiddenFields.find((field) => field.name === categoryName);
  };

  // Form state
  const [greenThreshold, setGreenThreshold] = useState<string>("20");
  const [yellowThreshold, setYellowThreshold] = useState<string>("10");
  const [selectedYearIds, setSelectedYearIds] = useState<number[]>([]);
  const [sortBy, setSortBy] = useState<"units" | "volume" | "netIncome">(
    "netIncome",
  );

  // Track auto-load — ref so it never causes a re-render
  const hasAutoLoaded = useRef(false);

  // Load settings with useQuery.
  // gcTime: 0 clears cache on unmount so navigate-back always gets fresh data.
  const { data: settingsData, isFetched: settingsFetched } = useQuery({
    queryKey: ["businessZonesSettings"],
    queryFn: getBusinessZonesSettings,
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // State flag that flips AFTER settings are applied to form state.
  // Changing this triggers a re-render where queryKey already has correct values.
  const [settingsApplied, setSettingsApplied] = useState(false);

  // Effect 1: Apply settings values to form state.
  // All setX calls + setSettingsApplied batch into ONE re-render.
  // We do NOT fetch here — queryKey still carries old/default values in this cycle.
  useEffect(() => {
    if (!settingsFetched || settingsApplied) return;

    if (settingsData) {
      setGreenThreshold(settingsData.greenThreshold.toString());
      setYellowThreshold(settingsData.yellowThreshold.toString());
      setSortBy(settingsData.zoneMetric as "units" | "volume" | "netIncome");

      if (settingsData.evaluationYears) {
        const yearIds = settingsData.evaluationYears
          .split(",")
          .filter((id) => id.trim())
          .map((id) => parseInt(id.trim(), 10));
        setSelectedYearIds(yearIds);
      }
    }

    // Batched with the state updates — React does ONE re-render with all new values
    setSettingsApplied(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsFetched]);

  // Validation - must be before queries that use these values
  const greenValue = parseFloat(greenThreshold) || 0;
  const yellowValue = parseFloat(yellowThreshold) || 0;
  const isValid = greenValue > yellowValue && yellowValue > 0;

  // Track if settings have changed from loaded values
  const hasSettingsChanged = useMemo(() => {
    if (!settingsData) return true; // No loaded settings, save on first use

    return (
      greenValue !== settingsData.greenThreshold ||
      yellowValue !== settingsData.yellowThreshold ||
      sortBy !== settingsData.zoneMetric ||
      selectedYearIds.join(",") !== settingsData.evaluationYears
    );
  }, [settingsData, greenValue, yellowValue, sortBy, selectedYearIds]);

  // Use useQuery for business zones data (triggered manually)
  const {
    data: businessZonesData,
    isLoading,
    error: businessZonesError,
    refetch: fetchBusinessZones,
  } = useQuery({
    queryKey: [
      "businessZonesData",
      selectedYearIds,
      greenValue,
      yellowValue,
      sortBy,
      effectiveCoAgentIds,
    ],
    queryFn: () =>
      getBusinessZonesData(
        selectedYearIds,
        greenValue,
        yellowValue,
        sortBy,
        effectiveCoAgentIds,
      ),
    enabled: false, // Manual trigger only via button or initial load
    gcTime: 0, // Don't cache — always fetch fresh on refetch
    refetchOnWindowFocus: false,
  });

  // Effect 2: Auto-fetch once, fires AFTER Effect 1's re-render.
  // By this point queryKey already reflects the settings values.
  useEffect(() => {
    if (!settingsApplied || hasAutoLoaded.current || !isValid) return;
    hasAutoLoaded.current = true;
    fetchBusinessZones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsApplied, isValid]);

  // Save settings mutation
  const saveSettingsMutation = useMutation({
    mutationFn: (settings: any) => saveBusinessZonesSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["businessZonesSettings"] });
    },
    onError: (error) => {
      console.error("Failed to save business zones settings:", error);
    },
  });

  const handleAnalyzeZones = () => {
    if (!isValid) return;

    // Trigger the query
    fetchBusinessZones();

    // Only save settings if they have changed
    if (hasSettingsChanged) {
      saveSettingsMutation.mutate({
        greenThreshold: greenValue,
        yellowThreshold: yellowValue,
        evaluationYears: selectedYearIds.join(","),
        zoneMetric: sortBy,
      });
    }
  };

  const handleReset = () => {
    setGreenThreshold("20");
    setYellowThreshold("10");
    setSelectedYearIds([]);
    setSortBy("netIncome");
    setPageCoAgentIds([]);
  };

  return (
    <PageLayout>
      <LoadingDialog open={isLoading} message="Analyzing Business Zones..." />

      <Box sx={{ mb: 6, ml: 1 }}>
        <Typography
          variant="h4"
          color="primary"
          component="h1"
          fontWeight="bold"
          gutterBottom
        >
          Business Zones
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3, lineHeight: 1.6 }}
        >
          Not every opportunity deserves the same yes. Some fall into your Green
          Zone, where it feels right to go. Others land in your Yellow Zone—a
          place to slow down and think. And a few show up in your Red Zone,
          where it may be best to stop. Business Zones helps you clearly see
          where each opportunity fits, so you can decide with confidence and
          clarity.
        </Typography>

        {/* Important Note */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Note that the following analysis only accounts for{" "}
            <strong>Seller</strong> and <strong>Buyer</strong> transactions.
          </Typography>
        </Alert>

        {/* Input Form Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={3}>
            {/* Green Zone Threshold */}
            <Grid item xs={12} md={4}>
              <NumericFormat
                fullWidth
                valueIsNumericString
                customInput={TextField}
                onValueChange={(values) => setGreenThreshold(values.value)}
                allowLeadingZeros={false}
                decimalScale={0}
                value={greenThreshold}
                label="Green Zone Starts At (%)"
                allowNegative={false}
                placeholder="20"
                helperText="Percentage threshold for Green Zone"
                isAllowed={(values) => {
                  const { floatValue } = values;
                  return (
                    floatValue === undefined ||
                    (floatValue >= 1 && floatValue <= 100)
                  );
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>

            {/* Yellow Zone Threshold */}
            <Grid item xs={12} md={4}>
              <NumericFormat
                fullWidth
                valueIsNumericString
                customInput={TextField}
                onValueChange={(values) => setYellowThreshold(values.value)}
                allowLeadingZeros={false}
                decimalScale={0}
                value={yellowThreshold}
                label="Yellow Zone Starts At (%)"
                allowNegative={false}
                placeholder="10"
                helperText="Percentage threshold for Yellow Zone"
                isAllowed={(values) => {
                  const { floatValue } = values;
                  return (
                    floatValue === undefined ||
                    (floatValue >= 0 && floatValue <= 100)
                  );
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>

            {/* Year Selection */}
            <Grid item xs={12} md={4}>
              <YearFilterSelect
                years={years}
                selectedYearIds={selectedYearIds}
                onChange={setSelectedYearIds}
                label="Select Years to Evaluate"
                size="medium"
              />
            </Grid>

            {/* Page-level Co-Agent Filter (hidden when global filter is active or coAgent category is hidden) */}
            {!isGlobalFilterActive &&
              !hiddenFields?.some((field) => field.name === "coAgent") && (
                <Grid item xs={12} md={4}>
                  <CoAgentFilterSelect
                    coAgents={coAgents}
                    selectedCoAgentIds={effectiveCoAgentIds}
                    onChange={setPageCoAgentIds}
                    label="Filter by Team Members"
                    size="medium"
                  />
                </Grid>
              )}

            {/* Validation Message */}
            {!isValid && (greenThreshold || yellowThreshold) && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  Green Zone percentage must be greater than Yellow Zone
                  percentage, and both must be greater than 0.
                </Alert>
              </Grid>
            )}

            {/* Sort By */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Sort Variables By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort Variables By"
                  onChange={(e) =>
                    setSortBy(
                      e.target.value as "units" | "volume" | "netIncome",
                    )
                  }
                >
                  <MenuItem value="netIncome">Net Income</MenuItem>
                  <MenuItem value="volume">Volume</MenuItem>
                  <MenuItem value="units">Units</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<TrafficIcon />}
                  onClick={handleAnalyzeZones}
                  disabled={!isValid || isLoading}
                >
                  Analyze Zones
                </Button>
                <Button variant="outlined" size="large" onClick={handleReset}>
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Error Message */}
        {businessZonesError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {businessZonesError.message ||
              "An error occurred while loading data"}
          </Alert>
        )}

        {/* Results Section */}
        {businessZonesData && (
          <Box>
            {/* Green Zone */}
            <Paper
              elevation={2}
              sx={{
                p: 3,
                mb: 3,
                backgroundColor: "#e8f5e9",
                border: "2px solid #4caf50",
              }}
            >
              <Typography
                variant="h5"
                color="success.main"
                fontWeight="bold"
                gutterBottom
              >
                🟢 Green Zone (≥{greenThreshold}%)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                High-performing variables that meet or exceed your green
                threshold
              </Typography>

              {businessZonesData.greenZone?.categories
                ?.filter((category) => isCategoryVisible(category.category))
                .map((category) => {
                  const variables = category.variables || [];
                  if (variables.length === 0) return null;

                  return (
                    <Paper
                      key={category.category}
                      elevation={0}
                      sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: "#fff",
                        border: "1px solid #a5d6a7",
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                          mb: 1,
                          textTransform: "capitalize",
                          color: "success.dark",
                        }}
                      >
                        {category.category === "customArea"
                          ? "Custom Area"
                          : category.category === "specialGroups"
                            ? "Special Groups"
                            : category.category === "specialTags"
                              ? "Special Tags"
                              : category.category === "zipCode"
                                ? "Zip Code"
                                : category.category}
                      </Typography>

                      <Box sx={{ overflowX: "auto" }}>
                        <table
                          className="business-zones-table"
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            tableLayout: "fixed",
                          }}
                        >
                          <colgroup>
                            <col style={{ width: "24%" }} />
                            <col style={{ width: "12%" }} />
                            <col style={{ width: "12%" }} />
                            <col style={{ width: "12%" }} />
                            <col style={{ width: "12%" }} />
                            <col style={{ width: "12%" }} />
                            <col style={{ width: "12%" }} />
                          </colgroup>
                          <thead>
                            <tr style={{ borderBottom: "2px solid #4caf50" }}>
                              <th style={{ textAlign: "left", padding: "8px" }}>
                                Name
                              </th>
                              <th
                                className="hide-on-mobile"
                                style={{
                                  textAlign: "right",
                                  padding: "8px",
                                  fontWeight: "normal",
                                }}
                              >
                                Units
                              </th>
                              <th
                                style={{
                                  textAlign: "right",
                                  padding: "8px",
                                  fontWeight: "bold",
                                  backgroundColor:
                                    sortBy === "units"
                                      ? "rgba(76, 175, 80, 0.1)"
                                      : "transparent",
                                }}
                              >
                                Units %{" "}
                                {sortBy === "units" && (
                                  <ArrowDownward
                                    sx={{
                                      fontSize: 16,
                                      ml: 0.5,
                                      verticalAlign: "middle",
                                    }}
                                  />
                                )}
                              </th>
                              <th
                                className="hide-on-mobile"
                                style={{
                                  textAlign: "right",
                                  padding: "8px",
                                  fontWeight: "normal",
                                }}
                              >
                                Volume
                              </th>
                              <th
                                style={{
                                  textAlign: "right",
                                  padding: "8px",
                                  fontWeight: "bold",
                                  backgroundColor:
                                    sortBy === "volume"
                                      ? "rgba(76, 175, 80, 0.1)"
                                      : "transparent",
                                }}
                              >
                                Volume %{" "}
                                {sortBy === "volume" && (
                                  <ArrowDownward
                                    sx={{
                                      fontSize: 16,
                                      ml: 0.5,
                                      verticalAlign: "middle",
                                    }}
                                  />
                                )}
                              </th>
                              <th
                                className="hide-on-mobile"
                                style={{
                                  textAlign: "right",
                                  padding: "8px",
                                  fontWeight: "normal",
                                }}
                              >
                                Net Income
                              </th>
                              <th
                                style={{
                                  textAlign: "right",
                                  padding: "8px",
                                  fontWeight: "bold",
                                  backgroundColor:
                                    sortBy === "netIncome"
                                      ? "rgba(76, 175, 80, 0.1)"
                                      : "transparent",
                                }}
                              >
                                Net Income %{" "}
                                {sortBy === "netIncome" && (
                                  <ArrowDownward
                                    sx={{
                                      fontSize: 16,
                                      ml: 0.5,
                                      verticalAlign: "middle",
                                    }}
                                  />
                                )}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...variables]
                              .sort((a, b) => {
                                if (sortBy === "units")
                                  return (
                                    (b.unitsPercent || 0) -
                                    (a.unitsPercent || 0)
                                  );
                                if (sortBy === "volume")
                                  return (
                                    (b.volumePercent || 0) -
                                    (a.volumePercent || 0)
                                  );
                                return (
                                  (b.netIncomePercent || 0) -
                                  (a.netIncomePercent || 0)
                                );
                              })
                              .map((variable) => (
                                <tr
                                  key={variable.id}
                                  style={{ borderBottom: "1px solid #e0e0e0" }}
                                >
                                  <td style={{ padding: "8px" }}>
                                    {variable.name}
                                  </td>
                                  <td
                                    className="hide-on-mobile"
                                    style={{
                                      textAlign: "right",
                                      padding: "8px",
                                    }}
                                  >
                                    {variable.units?.toLocaleString("en-US", {
                                      maximumFractionDigits: 0,
                                    })}
                                  </td>
                                  <td
                                    style={{
                                      textAlign: "right",
                                      padding: "8px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {variable.unitsPercent?.toFixed(2)}%
                                  </td>
                                  <td
                                    className="hide-on-mobile"
                                    style={{
                                      textAlign: "right",
                                      padding: "8px",
                                    }}
                                  >
                                    {formatCurrency(variable.volume)}
                                  </td>
                                  <td
                                    style={{
                                      textAlign: "right",
                                      padding: "8px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {variable.volumePercent?.toFixed(2)}%
                                  </td>
                                  <td
                                    className="hide-on-mobile"
                                    style={{
                                      textAlign: "right",
                                      padding: "8px",
                                    }}
                                  >
                                    {formatCurrency(variable.netIncome)}
                                  </td>
                                  <td
                                    style={{
                                      textAlign: "right",
                                      padding: "8px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {variable.netIncomePercent?.toFixed(2)}%
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </Box>
                    </Paper>
                  );
                })}

              {businessZonesData.greenZone?.categories?.every(
                (c) => !c.variables || c.variables.length === 0,
              ) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontStyle="italic"
                >
                  No variables meet the green zone threshold
                </Typography>
              )}
            </Paper>

            {/* Yellow Zone */}
            <Paper
              elevation={2}
              sx={{
                p: 3,
                mb: 3,
                backgroundColor: "#fff9e6",
                border: "2px solid #ffa726",
              }}
            >
              <Typography
                variant="h5"
                sx={{ color: "#f57c00" }}
                fontWeight="bold"
                gutterBottom
              >
                🟡 Yellow Zone (≥{yellowThreshold}% and &lt;{greenThreshold}%)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Moderate-performing variables that require consideration
              </Typography>

              {businessZonesData.yellowZone?.categories
                ?.filter((category) => isCategoryVisible(category.category))
                .map((category) => {
                  const variables = category.variables || [];
                  if (variables.length === 0) return null;

                  return (
                    <Paper
                      key={category.category}
                      elevation={0}
                      sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: "#fff",
                        border: "1px solid #ffe0b2",
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                          mb: 1,
                          textTransform: "capitalize",
                          color: "warning.dark",
                        }}
                      >
                        {category.category === "customArea"
                          ? "Custom Area"
                          : category.category === "specialGroups"
                            ? "Special Groups"
                            : category.category === "specialTags"
                              ? "Special Tags"
                              : category.category === "zipCode"
                                ? "Zip Code"
                                : category.category}
                      </Typography>

                      <Box sx={{ overflowX: "auto" }}>
                        <table
                          className="business-zones-table"
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            tableLayout: "fixed",
                          }}
                        >
                          <colgroup>
                            <col style={{ width: "24%" }} />
                            <col style={{ width: "12%" }} />
                            <col style={{ width: "12%" }} />
                            <col style={{ width: "12%" }} />
                            <col style={{ width: "12%" }} />
                            <col style={{ width: "12%" }} />
                            <col style={{ width: "12%" }} />
                          </colgroup>
                          <thead>
                            <tr style={{ borderBottom: "2px solid #ffa726" }}>
                              <th style={{ textAlign: "left", padding: "8px" }}>
                                Name
                              </th>
                              <th
                                className="hide-on-mobile"
                                style={{
                                  textAlign: "right",
                                  padding: "8px",
                                  fontWeight: "normal",
                                }}
                              >
                                Units
                              </th>
                              <th
                                style={{
                                  textAlign: "right",
                                  padding: "8px",
                                  fontWeight: "bold",
                                  backgroundColor:
                                    sortBy === "units"
                                      ? "rgba(255, 167, 38, 0.1)"
                                      : "transparent",
                                }}
                              >
                                Units %{" "}
                                {sortBy === "units" && (
                                  <ArrowDownward
                                    sx={{
                                      fontSize: 16,
                                      ml: 0.5,
                                      verticalAlign: "middle",
                                    }}
                                  />
                                )}
                              </th>
                              <th
                                className="hide-on-mobile"
                                style={{
                                  textAlign: "right",
                                  padding: "8px",
                                  fontWeight: "normal",
                                }}
                              >
                                Volume
                              </th>
                              <th
                                style={{
                                  textAlign: "right",
                                  padding: "8px",
                                  fontWeight: "bold",
                                  backgroundColor:
                                    sortBy === "volume"
                                      ? "rgba(255, 167, 38, 0.1)"
                                      : "transparent",
                                }}
                              >
                                Volume %{" "}
                                {sortBy === "volume" && (
                                  <ArrowDownward
                                    sx={{
                                      fontSize: 16,
                                      ml: 0.5,
                                      verticalAlign: "middle",
                                    }}
                                  />
                                )}
                              </th>
                              <th
                                className="hide-on-mobile"
                                style={{
                                  textAlign: "right",
                                  padding: "8px",
                                  fontWeight: "normal",
                                }}
                              >
                                Net Income
                              </th>
                              <th
                                style={{
                                  textAlign: "right",
                                  padding: "8px",
                                  fontWeight: "bold",
                                  backgroundColor:
                                    sortBy === "netIncome"
                                      ? "rgba(255, 167, 38, 0.1)"
                                      : "transparent",
                                }}
                              >
                                Net Income %{" "}
                                {sortBy === "netIncome" && (
                                  <ArrowDownward
                                    sx={{
                                      fontSize: 16,
                                      ml: 0.5,
                                      verticalAlign: "middle",
                                    }}
                                  />
                                )}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...variables]
                              .sort((a, b) => {
                                if (sortBy === "units")
                                  return (
                                    (b.unitsPercent || 0) -
                                    (a.unitsPercent || 0)
                                  );
                                if (sortBy === "volume")
                                  return (
                                    (b.volumePercent || 0) -
                                    (a.volumePercent || 0)
                                  );
                                return (
                                  (b.netIncomePercent || 0) -
                                  (a.netIncomePercent || 0)
                                );
                              })
                              .map((variable) => (
                                <tr
                                  key={variable.id}
                                  style={{ borderBottom: "1px solid #e0e0e0" }}
                                >
                                  <td style={{ padding: "8px" }}>
                                    {variable.name}
                                  </td>
                                  <td
                                    className="hide-on-mobile"
                                    style={{
                                      textAlign: "right",
                                      padding: "8px",
                                    }}
                                  >
                                    {variable.units?.toLocaleString("en-US", {
                                      maximumFractionDigits: 0,
                                    })}
                                  </td>
                                  <td
                                    style={{
                                      textAlign: "right",
                                      padding: "8px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {variable.unitsPercent?.toFixed(2)}%
                                  </td>
                                  <td
                                    className="hide-on-mobile"
                                    style={{
                                      textAlign: "right",
                                      padding: "8px",
                                    }}
                                  >
                                    {formatCurrency(variable.volume)}
                                  </td>
                                  <td
                                    style={{
                                      textAlign: "right",
                                      padding: "8px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {variable.volumePercent?.toFixed(2)}%
                                  </td>
                                  <td
                                    className="hide-on-mobile"
                                    style={{
                                      textAlign: "right",
                                      padding: "8px",
                                    }}
                                  >
                                    {formatCurrency(variable.netIncome)}
                                  </td>
                                  <td
                                    style={{
                                      textAlign: "right",
                                      padding: "8px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {variable.netIncomePercent?.toFixed(2)}%
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </Box>
                    </Paper>
                  );
                })}

              {businessZonesData.yellowZone?.categories?.every(
                (c) => !c.variables || c.variables.length === 0,
              ) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontStyle="italic"
                >
                  No variables in the yellow zone range
                </Typography>
              )}
            </Paper>

            {/* Red Zone */}
            <Paper
              elevation={2}
              sx={{
                p: 3,
                mb: 3,
                backgroundColor: "#ffebee",
                border: "2px solid #f44336",
              }}
            >
              <Typography
                variant="h5"
                color="error.main"
                fontWeight="bold"
                gutterBottom
              >
                🔴 Red Zone (&lt;{yellowThreshold}%)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Low-performing variables that may not be worth your focus.
                Variables contributing less than 1% are not displayed.
              </Typography>

              {businessZonesData.redZone?.categories
                ?.filter((category) => isCategoryVisible(category.category))
                .map((category) => {
                  const allVariables = category.variables || [];
                  const variables = allVariables.filter((v) => {
                    if (sortBy === "units") return (v.unitsPercent || 0) >= 1;
                    if (sortBy === "volume") return (v.volumePercent || 0) >= 1;
                    return (v.netIncomePercent || 0) >= 1.0;
                  });
                  if (variables.length === 0) return null;

                  return (
                    <Paper
                      key={category.category}
                      elevation={0}
                      sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: "#fff",
                        border: "1px solid #ffcdd2",
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                          mb: 1,
                          textTransform: "capitalize",
                          color: "error.dark",
                        }}
                      >
                        {category.category === "customArea"
                          ? "Custom Area"
                          : category.category === "specialGroups"
                            ? "Special Groups"
                            : category.category === "specialTags"
                              ? "Special Tags"
                              : category.category === "zipCode"
                                ? "Zip Code"
                                : category.category}
                      </Typography>

                      <Box sx={{ overflowX: "auto" }}>
                        <table
                          className="business-zones-table"
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            tableLayout: "fixed",
                          }}
                        >
                          <colgroup>
                            <col style={{ width: "24%" }} />
                            <col style={{ width: "12%" }} />
                            <col style={{ width: "12%" }} />
                            <col style={{ width: "12%" }} />
                            <col style={{ width: "12%" }} />
                            <col style={{ width: "12%" }} />
                            <col style={{ width: "12%" }} />
                          </colgroup>
                          <thead>
                            <tr style={{ borderBottom: "2px solid #f44336" }}>
                              <th style={{ textAlign: "left", padding: "8px" }}>
                                Name
                              </th>
                              <th
                                className="hide-on-mobile"
                                style={{
                                  textAlign: "right",
                                  padding: "8px",
                                  fontWeight: "normal",
                                }}
                              >
                                Units
                              </th>
                              <th
                                style={{
                                  textAlign: "right",
                                  padding: "8px",
                                  fontWeight: "bold",
                                  backgroundColor:
                                    sortBy === "units"
                                      ? "rgba(244, 67, 54, 0.1)"
                                      : "transparent",
                                }}
                              >
                                Units %{" "}
                                {sortBy === "units" && (
                                  <ArrowDownward
                                    sx={{
                                      fontSize: 16,
                                      ml: 0.5,
                                      verticalAlign: "middle",
                                    }}
                                  />
                                )}
                              </th>
                              <th
                                className="hide-on-mobile"
                                style={{
                                  textAlign: "right",
                                  padding: "8px",
                                  fontWeight: "normal",
                                }}
                              >
                                Volume
                              </th>
                              <th
                                style={{
                                  textAlign: "right",
                                  padding: "8px",
                                  fontWeight: "bold",
                                  backgroundColor:
                                    sortBy === "volume"
                                      ? "rgba(244, 67, 54, 0.1)"
                                      : "transparent",
                                }}
                              >
                                Volume %{" "}
                                {sortBy === "volume" && (
                                  <ArrowDownward
                                    sx={{
                                      fontSize: 16,
                                      ml: 0.5,
                                      verticalAlign: "middle",
                                    }}
                                  />
                                )}
                              </th>
                              <th
                                className="hide-on-mobile"
                                style={{
                                  textAlign: "right",
                                  padding: "8px",
                                  fontWeight: "normal",
                                }}
                              >
                                Net Income
                              </th>
                              <th
                                style={{
                                  textAlign: "right",
                                  padding: "8px",
                                  fontWeight: "bold",
                                  backgroundColor:
                                    sortBy === "netIncome"
                                      ? "rgba(244, 67, 54, 0.1)"
                                      : "transparent",
                                }}
                              >
                                Net Income %{" "}
                                {sortBy === "netIncome" && (
                                  <ArrowDownward
                                    sx={{
                                      fontSize: 16,
                                      ml: 0.5,
                                      verticalAlign: "middle",
                                    }}
                                  />
                                )}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...variables]
                              .sort((a, b) => {
                                if (sortBy === "units")
                                  return (
                                    (b.unitsPercent || 0) -
                                    (a.unitsPercent || 0)
                                  );
                                if (sortBy === "volume")
                                  return (
                                    (b.volumePercent || 0) -
                                    (a.volumePercent || 0)
                                  );
                                return (
                                  (b.netIncomePercent || 0) -
                                  (a.netIncomePercent || 0)
                                );
                              })
                              .map((variable) => (
                                <tr
                                  key={variable.id}
                                  style={{ borderBottom: "1px solid #e0e0e0" }}
                                >
                                  <td style={{ padding: "8px" }}>
                                    {variable.name}
                                  </td>
                                  <td
                                    className="hide-on-mobile"
                                    style={{
                                      textAlign: "right",
                                      padding: "8px",
                                    }}
                                  >
                                    {variable.units?.toLocaleString("en-US", {
                                      maximumFractionDigits: 0,
                                    })}
                                  </td>
                                  <td
                                    style={{
                                      textAlign: "right",
                                      padding: "8px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {variable.unitsPercent?.toFixed(2)}%
                                  </td>
                                  <td
                                    className="hide-on-mobile"
                                    style={{
                                      textAlign: "right",
                                      padding: "8px",
                                    }}
                                  >
                                    {formatCurrency(variable.volume)}
                                  </td>
                                  <td
                                    style={{
                                      textAlign: "right",
                                      padding: "8px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {variable.volumePercent?.toFixed(2)}%
                                  </td>
                                  <td
                                    className="hide-on-mobile"
                                    style={{
                                      textAlign: "right",
                                      padding: "8px",
                                    }}
                                  >
                                    {formatCurrency(variable.netIncome)}
                                  </td>
                                  <td
                                    style={{
                                      textAlign: "right",
                                      padding: "8px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {variable.netIncomePercent?.toFixed(2)}%
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </Box>
                    </Paper>
                  );
                })}

              {businessZonesData.redZone?.categories?.every(
                (c) => !c.variables || c.variables.length === 0,
              ) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontStyle="italic"
                >
                  No variables in the red zone
                </Typography>
              )}
            </Paper>
          </Box>
        )}
      </Box>
    </PageLayout>
  );
};

export default BusinessZones;
